import React, { useLayoutEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Box, Button, Backdrop, Modal, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { CloseIcon } from '@icons';
import { ReactComponent as PrivacyIcon } from '@public/images/privacy_badge.svg';
import PdfViewer from './PdfViewer';

import { ModalView } from './interfaces';
import { useStyles } from './styles';

const PolicyModalView = ({
  file,
  markdown,
  open,
  disabled,
  isRead,
  handleScroll,
  onReachEnd,
  onClose,
  onClick,
  isAccepted,
  t: translate,
}: ModalView) => {
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Short markdown may never produce a scroll event, so it would never count as
  // read. When the content fits without scrolling, mark it read immediately.
  useLayoutEffect(() => {
    if (!markdown) {
      return;
    }

    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 30) {
      onReachEnd?.();
    }
  }, [markdown, onReachEnd]);

  return (
    <Modal
      sx={classes.root}
      open={open}
      components={{ Backdrop }}
      hideBackdrop={isAccepted}
    >
      <Box component="div" sx={classes.container}>
        {isAccepted ? (
          <CloseIcon sx={classes.closeIcon} onClick={onClose} />
        ) : null}
        <Box component="div" sx={classes.iconContainer}>
          <PrivacyIcon />
        </Box>
        <Typography sx={classes.title}>
          {translate('agreement.title')}
        </Typography>
        {markdown ? (
          <Box ref={scrollRef} sx={classes.markdownScroll} onScroll={handleScroll}>
            <Box sx={classes.markdownContent}>
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </Box>
            {isRead ? null : <Box sx={classes.overlay} />}
          </Box>
        ) : (
          <Box sx={classes.pdf} onScroll={handleScroll}>
            {file ? <PdfViewer file={file} /> : null}
            {isRead ? null : <Box sx={classes.overlay} />}
          </Box>
        )}

        {!isAccepted ? (
          <Button sx={classes.button} onClick={onClick} disabled={disabled}>
            {translate('agreement.accept')}
          </Button>
        ) : null}
      </Box>
    </Modal>
  );
};

export default withTranslation()(PolicyModalView);
